/* eslint-disable max-lines */
/* eslint-disable max-lines-per-function */
import { inject, Injectable, signal } from '@angular/core';

import { BillingPlanTier } from '@core/billing-types';
import { FrontendOnlyGuardedFeature } from '@core/feature-guard-legacy';
import { I18nService } from '@core/i18n-legacy';
import { IntegrationKinds } from '@core/integrations-types';
import {
  IconName,
  SelectGroupedOptions,
  SelectGroupOption,
  SelectOption,
} from '@core/user-interface-legacy';
import { camelCase } from '@core/utils-legacy';
import {
  ActionReferences,
  AutomationAction,
  AutomationActionType,
  AutomationConditionType,
  AutomationTriggerType,
  TriggerCategory,
  TriggerDataOption,
  TriggersOptionsData,
  WorkflowAutomationOptionsData,
} from '@core/workflow-automations-types';
import {
  ActionOption,
  ConditionOption,
  TriggerOptionData,
} from '@recruitee/workflow-automations-types';

import { AutomationAccessService } from './automation-access.service';
import { AutomationsAccessoryDataService } from './automations-accessory-data.service';
import { AutomationsFeatureGuardService } from './automations-feature-guard.service';
import { AutomationWizardDataContext } from './automations-wizard.facade';

type TriggerActionsMap = Record<AutomationTriggerType, ActionOption[]>;

type TriggerConditionsMap = Record<AutomationTriggerType, ConditionOption[]>;

type TriggerDataOptionsMap = Record<AutomationTriggerType, TriggerOptionData[]>;
type TriggerSelectGroupedOptions = SelectGroupedOptions & {
  category: TriggerCategory;
};

@Injectable()
export class AutomationOptionsService {
  private readonly i18n = inject(I18nService);
  private readonly accessoryDataService = inject(AutomationsAccessoryDataService);
  private readonly accessService = inject(AutomationAccessService);
  private readonly automationsFeatureGuardService = inject(AutomationsFeatureGuardService);

  private readonly allPlansActions = [
    AutomationActionType.CandidateSendEmail,
    AutomationActionType.CandidateAddNote,
    AutomationActionType.CandidateAddTask,
    AutomationActionType.CandidateAddTags,
    AutomationActionType.CandidateEvaluationRequest,
    AutomationActionType.CandidateAssignTo,
    AutomationActionType.CandidateAddFollowers,
  ];
  private readonly advancePlanActions = [
    AutomationActionType.CandidateSendToTellentHR,
    AutomationActionType.CandidateUnassignFromJob,
    AutomationActionType.CandidateCancelScheduledEvents,
    AutomationActionType.CandidateCancelEvaluationRequests,
  ];

  public dynamicOptionDataTriggerTypes = [
    AutomationTriggerType.CandidateTagAdded,
    AutomationTriggerType.JobTagAdded,
  ];
  public dynamicOptionDataConditionTypes = [
    AutomationConditionType.CandidateTags,
    AutomationConditionType.CandidateSourceTags,
  ];

  public triggerOptions = signal<TriggerSelectGroupedOptions[]>([]);
  public currentTriggerDataOptions = signal<TriggerOptionData[] | null>(null);
  public currentConditionsOptions = signal<ConditionOption[]>([]);
  public currentActionOptions = signal<ActionOption[]>([]);
  public allActionsDataMap = signal<Record<AutomationActionType, ActionOption> | null>(null);
  public actionReferencesMap = signal<Record<string, ActionReferences>>({});

  private dataOptionsReferencesMap = signal<Record<string, SelectOption[]>>({});
  private triggerConditionsMap = signal<TriggerConditionsMap | null>(null);
  private triggerActionsMap = signal<TriggerActionsMap | null>(null);
  private triggerDataOptionsMap = signal<TriggerDataOptionsMap | null>(null);
  private groupTranslations: Record<TriggerCategory, string> = {
    [TriggerCategory.Candidate]: this.i18n.translate('app.common.candidate'),
    [TriggerCategory.Job]: this.i18n.translate('app.plurals.job.one'),
  };
  private translations = {
    noTellentIntegrationTooltip: this.i18n.translate(
      'app.workflow_automations.automations_wizard_dialog.actions.send_to_tellent_hr.no_integration_tooltip',
    ),
  };

  public setAllOptionsData(
    optionsData: WorkflowAutomationOptionsData,
    dataContext: AutomationWizardDataContext | null,
  ) {
    const allTriggersData = optionsData.triggers;
    this.dataOptionsReferencesMap.set(optionsData.dataOptions);

    this.setAllActionOptionsMap(allTriggersData);
    this.setTriggerOptions(allTriggersData, dataContext);
    this.setTriggerConditionsMap(allTriggersData);
    this.setTriggerActionsMap(allTriggersData);
    this.setTriggerDataOptionsMap(allTriggersData);
  }

  public setReferencesMap(action: AutomationAction): void {
    if (!action.references || !action.uuid) {
      return;
    }
    const currentMap = this.actionReferencesMap();
    this.actionReferencesMap.set({
      ...currentMap,
      [action.uuid]: action.references,
    });
  }

  public setCurrentOptionsData(triggerType: AutomationTriggerType): void {
    this.currentTriggerDataOptions.set(this.getTriggerDataOptions(triggerType));
    this.currentActionOptions.set(this.getActionOptions(triggerType));
    this.currentConditionsOptions.set(this.getConditionsOptions(triggerType));
  }

  public getGroupTranslation(category: TriggerCategory): string {
    return this.groupTranslations[category];
  }

  private getConditionsOptions(triggerType: AutomationTriggerType): ConditionOption[] {
    return this.triggerConditionsMap()?.[triggerType] || [];
  }

  private getActionOptions(triggerType: AutomationTriggerType): ActionOption[] {
    return this.triggerActionsMap()?.[triggerType] || [];
  }

  private getTriggerDataOptions(triggerType: AutomationTriggerType): TriggerOptionData[] | null {
    return this.triggerDataOptionsMap()?.[triggerType] || null;
  }

  private setTriggerOptions(
    optionsData: TriggersOptionsData,
    dataContext: AutomationWizardDataContext | null,
  ) {
    if (this.accessService.triggersHaveUpsell) {
      const availableTriggers = optionsData.filter(trigger => !trigger.isUpsell);
      const upsellTriggers = optionsData.filter(trigger => trigger.isUpsell);
      const candidateCategoryOptions = [
        ...availableTriggers.map(trigger => ({
          value: trigger.type,
          icon: trigger.iconName,
          label: trigger.name,
        })),
        ...(dataContext !== AutomationWizardDataContext.DisqualifyReason
          ? [this.getDisabledCandidateDisqualifiedTrigger()]
          : []),
      ];
      const triggerGroupSelectOptions: TriggerSelectGroupedOptions[] = [
        {
          title: null,
          options: candidateCategoryOptions,
          category: TriggerCategory.Candidate,
        },
        ...(upsellTriggers.length
          ? [
              {
                id: 'upsell',
                title: this.i18n.translate(
                  'app.workflow_automations.automations_wizard_dialog.available_in_optimize',
                ),
                options: upsellTriggers.map(trigger => ({
                  value: trigger.type,
                  icon: trigger.iconName,
                  label: trigger.name,
                  disabled: true,
                  clickHandler: () =>
                    this.automationsFeatureGuardService.getAutomationsFeatureGuardDialog(
                      FrontendOnlyGuardedFeature.WorkflowAutomationsOptimize,
                    ),
                })),
                category: TriggerCategory.Candidate,
              },
            ]
          : []),
      ];
      this.triggerOptions.set(triggerGroupSelectOptions);
    } else {
      const triggerGroupsMap = optionsData.reduce(
        (acc, trigger) => {
          const { category, type: value, iconName: icon, name: label } = trigger;
          acc[category].push({ value, icon, label });
          return acc;
        },
        <Record<TriggerCategory, SelectGroupOption[]>>{
          [TriggerCategory.Candidate]: [],
          [TriggerCategory.Job]: [],
        },
      );
      const triggerGroupSelectOptions: TriggerSelectGroupedOptions[] = Object.entries(
        triggerGroupsMap,
      ).map(([category, options]) => ({
        title: this.groupTranslations[<TriggerCategory>category],
        options:
          dataContext !== AutomationWizardDataContext.DisqualifyReason
            ? this.addCandidateDisqualifiedTriggerOptionIfNeeded(<TriggerCategory>category, options)
            : options,
        category: <TriggerCategory>category,
      }));
      this.triggerOptions.set(triggerGroupSelectOptions);
    }
  }

  private setTriggerConditionsMap(optionsData: TriggersOptionsData) {
    this.triggerConditionsMap.set(
      optionsData.reduce(
        (acc, trigger) => {
          acc[trigger.type] = trigger.conditions.map(condition => {
            const options = this.getOptions(condition.dataOptions);
            return {
              type: condition.type,
              label: condition.name,
              iconName: condition.iconName,
              options,
            };
          });
          return acc;
        },
        <TriggerConditionsMap>{},
      ),
    );
  }

  private setTriggerActionsMap(optionsData: TriggersOptionsData) {
    this.triggerActionsMap.set(
      optionsData.reduce(
        (acc, trigger) => {
          acc[trigger.type] = trigger.actions.map(action => this.allActionsDataMap()![action.type]);
          return acc;
        },
        <TriggerActionsMap>{},
      ),
    );
  }

  private setTriggerDataOptionsMap(optionsData: TriggersOptionsData) {
    this.triggerDataOptionsMap.set(
      optionsData.reduce(
        (acc, trigger) => {
          const options = this.getOptions(trigger.dataOptions);

          if (trigger.dataOptions?.length) {
            acc[trigger.type] = <TriggerOptionData[]>options;
          }
          return acc;
        },
        <TriggerDataOptionsMap>{},
      ),
    );
  }

  private getOptions(dataOptions: TriggerDataOption[]): Array<{
    name: string;
    label: string;
    options: SelectOption[];
  }> {
    return dataOptions.map(dataOption => {
      const hasOptions = Array.isArray(dataOption.options);
      if (!hasOptions && (<{ reference: string }>dataOption.options)?.reference) {
        return {
          ...dataOption,
          options:
            this.dataOptionsReferencesMap()[
              camelCase((<{ reference: string }>dataOption.options).reference)
            ] || [],
        };
      }

      return { ...dataOption, options: <SelectOption[]>dataOption.options };
    });
  }

  private setAllActionOptionsMap(optionsData: TriggersOptionsData) {
    this.allActionsDataMap.set(
      optionsData.reduce(
        (acc, trigger) => {
          trigger.actions.forEach(action => {
            if (acc[action.type]) return;
            acc[action.type] = {
              type: action.type,
              label: action.name,
              icon: action.iconName || '',
              recommended: action.recommended,
              available: action.available,
              entitledFrom: this.getActionEntitledFrom(action.type),
              entitled: action.entitled,
              enableActionTooltip: !action.available
                ? this.i18n.translate(
                    'app.workflow_automations.automations_wizard_dialog.actions.no_permission_to_enable_action',
                  )
                : '',
              editActionTooltip: !action.available
                ? this.i18n.translate(
                    'app.workflow_automations.automations_wizard_dialog.actions.no_permission_to_edit_action',
                  )
                : '',
            };
            if (action.type === AutomationActionType.CandidateSendToTellentHR && action.available) {
              const hasTellentHrIntegrationEnabled = this.accessoryDataService
                .enabledIntegrations()
                ?.includes(IntegrationKinds.KIWI_HR);
              acc[action.type].available = !!hasTellentHrIntegrationEnabled;
              acc[action.type].enableActionTooltip = !hasTellentHrIntegrationEnabled
                ? this.translations.noTellentIntegrationTooltip
                : '';
            }
          });
          return acc;
        },
        <Record<AutomationActionType, ActionOption>>{},
      ),
    );
  }

  private getActionEntitledFrom(actionType: AutomationActionType): BillingPlanTier | null {
    if (this.allPlansActions.includes(actionType)) {
      return null;
    }
    if (this.advancePlanActions.includes(actionType)) {
      return BillingPlanTier.Grow;
    }
    return BillingPlanTier.Optimize;
  }

  private addCandidateDisqualifiedTriggerOptionIfNeeded(
    category: TriggerCategory,
    options: SelectGroupOption[],
  ) {
    if (category !== TriggerCategory.Candidate) {
      return options;
    }

    const candidateRequalifiedTriggerIndex = options.findIndex(
      option => option.value === AutomationTriggerType.CandidateRequalified,
    );

    if (candidateRequalifiedTriggerIndex === -1) {
      return options;
    }

    return [
      ...options.slice(0, candidateRequalifiedTriggerIndex + 1),
      this.getDisabledCandidateDisqualifiedTrigger(),
      ...options.slice(candidateRequalifiedTriggerIndex + 1),
    ];
  }

  private getDisabledCandidateDisqualifiedTrigger(): SelectGroupOption {
    return {
      value: AutomationTriggerType.CandidateDisqualified,
      icon: <IconName>'block',
      label: this.i18n.translate(
        'app.workflow_automations.automations_wizard_dialog.triggers.candidate_disqualified',
      ),
      disabled: true,
      tooltipHtml: this.i18n.translate(
        'app.workflow_automations.automations_wizard_dialog.triggers.candidate_disqualified_disabled_tooltip',
      ),
      iconColorToken: 'colorIconDisabled',
    };
  }
}
